/**
 * Small pure helpers shared across the profile components: name/initials/location
 * formatting and the URL `:page` ↔ tab mapping.
 */
import type { User } from '@/types/api';

/** Two-letter initials for an avatar fallback. */
export function initials(user?: Partial<User> | null): string {
  const f = user?.firstname?.[0] ?? '';
  const l = user?.lastname?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

/** "First Last", trimmed. */
export function fullName(user?: Partial<User> | null): string {
  return `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim();
}

/** "City, Country" (deduped, empty parts dropped). */
export function location(user?: Partial<User> | null): string {
  const parts = [user?.city, user?.state, user?.country].filter(Boolean) as string[];
  return Array.from(new Set(parts)).join(', ');
}

/**
 * Single source of truth for the gender emoji shown wherever a user's
 * gender is rendered (profile header, sticky bar, suggested-people rail,
 * friend / people cards, …). Centralised so swapping the icon set later
 * is a one-line change.
 *
 *   👨 — male
 *   👩 — female
 *   🧑 — other / unknown
 */
export function genderEmoji(
  value: string | null | undefined,
): '👨' | '👩' | '🧑' {
  const g = String(value ?? '').trim().toLowerCase();
  // Backend uses both 'M'/'F' (raw enum) and 'Male'/'Female' (extra label).
  if (g === 'm' || g.startsWith('male')) return '👨';
  if (g === 'f' || g.startsWith('female')) return '👩';
  return '🧑';
}

/**
 * Pull the best-available gender string off a user (extra.gender preferred,
 * raw `gender` as fallback). Returns `''` when both are missing.
 */
export function userGender(user: Partial<User> | null | undefined): string {
  if (user?.extra?.gender) return String(user.extra.gender);
  if (user?.gender)
    return user.gender === 'F' ? 'Female' : user.gender === 'M' ? 'Male' : 'Other';
  return '';
}

/**
 * Compose the two meta lines shown under a user's name on list-card grids
 * (People `/users` + Friends `/dashboard/friends`):
 *   line 1 → gender · age · relationship_status    (no "y" suffix on age)
 *   line 2 → city, country                          (state intentionally skipped)
 *
 * `emoji` mirrors `genderEmoji()` for callers that want to render the
 * cue separately (so it can take its own colour / size). Empty pieces
 * are dropped so a missing field never produces a stray separator.
 * `extra.relationship_status` is the pre-formatted API label (e.g.
 * "Single"); falls back to the raw `profile.relationship_status`.
 */
export function userMetaLines(
  user: Partial<User> | null | undefined,
): { emoji: string; line1: string; line2: string } {
  const top: string[] = [];
  const gender = userGender(user);
  if (gender) top.push(gender);
  if (user?.extra?.age) top.push(String(user.extra.age));
  const rel = user?.extra?.relationship_status || user?.profile?.relationship_status;
  if (rel) top.push(String(rel));

  const bottom = [user?.city, user?.country].filter(Boolean) as string[];

  return {
    emoji: gender ? genderEmoji(gender) : '',
    line1: top.join(' · '),
    line2: bottom.join(', '),
  };
}

/** The profile tabs, in display order. `timeline` maps to the bare `/user/:u`.
 *  Media holds both photos AND videos — there is no separate "Photos" tab. */
export const PROFILE_TABS = [
  'timeline',
  'about',
  'friends',
  'media',
  'blogs',
] as const;

export type ProfileTab = (typeof PROFILE_TABS)[number];

/**
 * Normalise a raw `:page` URL segment to a known tab. `photos`/`videos` both
 * fold into the Media tab (kept for back-compat with old `/user/:u/photos`
 * links). The deep-link `activity` segment resolves to `timeline`; anything
 * unknown (or absent) falls back to `timeline`.
 */
export function tabFromPage(page: string | undefined): ProfileTab {
  if (!page) return 'timeline';
  const lower = page.toLowerCase();
  if (lower === 'photos' || lower === 'videos') return 'media';
  if (lower === 'activity') return 'timeline';
  if ((PROFILE_TABS as readonly string[]).includes(lower)) {
    return lower as ProfileTab;
  }
  return 'timeline';
}

/** Build the parity URL for a tab (timeline = bare profile URL). */
export function tabHref(username: string, tab: ProfileTab): string {
  return tab === 'timeline' ? `/user/${username}` : `/user/${username}/${tab}`;
}
